import React from 'react';
import ReactDOM from 'react-dom';
import {
    Modal as ModalRN,
    Platform,
    Pressable,
    Switch as SwitchRN,
    Text as TextRN,
    TouchableOpacity as TouchableOpacityRN,
    TouchableWithoutFeedback as TouchableWithoutFeedbackRN
} from 'react-native';

// Simple Modal for web platform using Portal
const WebModal = ({ visible, children, onRequestClose, transparent, animationType, ...props }) => {
    if (!visible) return null;
    
    if (typeof document === 'undefined') return null;
    
    return ReactDOM.createPortal(
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9999,
                display: 'flex',
            }}
            onClick={(e) => {
                // Close only if clicked on the overlay itself
                if (e.target === e.currentTarget && onRequestClose) {
                    onRequestClose();
                }
            }}
        >
            {children}
        </div>,
        document.body
    );
};

export { Component, Fragment, PureComponent, default as React } from 'react';
export {
    ActivityIndicator,
    // Picker,
    Alert, AppState,
    BackHandler, Dimensions, FlatList,
    Image, Keyboard,
    Linking, Platform, RefreshControl, ScrollView, Share, StatusBar, StyleSheet, View
} from 'react-native';

export { Badge, Card, ListItem, SearchBar as SearchBarNative } from 'react-native-elements';
export { SafeAreaView } from 'react-native-safe-area-context';

export { Picker } from '@react-native-picker/picker';

export { ScrollView as KeyboardAwareScrollView } from 'react-native'; // -keyboard-aware-scroll-view
export { default as S } from '../styles';

const withPrefix = (ns, prefix = 'button') => (ns ? `${prefix}:${ns}` : prefix);
export const Text = React.memo((props) => (
    <TextRN
        {...props}
        accessibilityLabel={withPrefix(props.accessibilityLabel, 'text')}
    />
));
export const TouchableOpacity = React.memo((props) => (
    <TouchableOpacityRN
        {...props}
        accessibilityLabel={withPrefix(props.accessibilityLabel)}
    />
));

// Use Pressable instead of TouchableWithoutFeedback for mobile platforms
// For web, return the original TouchableWithoutFeedback
export const TouchableWithoutFeedback = React.memo((props) => {
    const { onPress, children, ...rest } = props;
    
    if (Platform.OS === 'web') {
        // On web, always use TouchableWithoutFeedback to prevent focus loss
        return (
            <TouchableWithoutFeedbackRN
                {...rest}
                onPress={onPress}
                accessibilityLabel={withPrefix(rest.accessibilityLabel)}
            >
                {children}
            </TouchableWithoutFeedbackRN>
        );
    }
    
    // For mobile platforms, use Pressable
    return (
        <Pressable
            {...rest}
            onPress={onPress}
            accessibilityLabel={withPrefix(rest.accessibilityLabel)}
            style={({ pressed }) => [
                rest.style, pressed && rest.pressedStyle
            ]}
        >
            {children}
        </Pressable>
    );
});

export const Switch = ({
    thumbTintColor,
    trackColor = { true: '#6987D6' },
    ...props
}) => {
    const extraProps = Platform.select({
        ios: {
            trackColor,
            ios_backgroundColor: '#E0E0E0',
        },
        android: {
            thumbColor: props.value ? thumbTintColor : 'white',
            trackColor,
        },
        default: {},
    });
    return (
        <SwitchRN
            activeTrackColor="#9FB9FF"
            {...props}
            {...extraProps}
        />
    );
};

Switch.Default = ({
    accessibilityLabel,
    style,
    activeThumbColor = '#4770C0',
    activeTrackColor = '#9FB9FF',
    onTintColor = Platform.OS !== 'web' ? '#6987D6' : '#8FB8FF',
    thumbTintColor = '#5C85DD',
    trackColor = {
        false: '#CFCFCF',
        true: Platform.OS === 'ios' ? '#5C85DD' : '#8FB8FF',
    },
    value,
    onValueChange,
}) => {
    return (
        <Switch
            accessibilityLabel={accessibilityLabel}
            style={[
                { ...style },
                {
                    transform: Platform.OS === 'ios' ? [ { scaleX: 0.8 }, { scaleY: 0.8 } ] : [],
                },
            ]}
            activeThumbColor={activeThumbColor}
            activeTrackColor={activeTrackColor}
            onTintColor={onTintColor}
            thumbTintColor={thumbTintColor}
            trackColor={trackColor}
            value={value}
            onValueChange={onValueChange}
        />
    );
};

/** iOS: stacked transparent RN Modals often render behind each other or not at all on older OS versions unless `overFullScreen` is used. */
const ModalIOSStackingSafe = (props) => {
    if (Platform.OS !== 'ios') {
        return <ModalRN {...props} />;
    }
    const stackingFix =
        props.transparent && props.presentationStyle == null
            ? { presentationStyle: 'overFullScreen' }
            : {};
    return <ModalRN {...props} {...stackingFix} />;
};

let ModalImpl2 = ModalIOSStackingSafe;
// Workaround for web:
if (Platform.OS === 'web') {
    /* global window */
    // Use our simple WebModal instead of modal-react-native-web
    ModalImpl2 = WebModal;
    
    if (window.Cypress) {
        ModalImpl2 = (props) => <div id="modal">{props.children}</div>;
    }

    Platform.select = ({
        web,
        android,
        default: deff,
    }) => web || deff || android;
}

export const ModalImpl = ModalImpl2;
